import type { Round } from '@pin/schemas/tournament';

const OSLO_TZ = 'Europe/Oslo';

// A broadcast day runs 08:00–07:59:59 local time. Shift back 8 h in UTC so
// the UTC date boundary aligns with that window (correct for CEST = UTC+2).
export function broadcastDayKey(round: Round): string {
  const ts = round.schedule?.locksAt;
  if (ts == null) return '';
  const shifted = new Date(ts - 8 * 60 * 60 * 1000);
  return shifted.toISOString().slice(0, 10);
}

// YYYY-MM-DD of the Monday of the ISO week that contains dayKey
export function weekMondayKey(dayKey: string): string {
  const d = new Date(`${dayKey}T12:00:00Z`);
  const dow = d.getUTCDay(); // 0 = Sun … 6 = Sat
  const toMonday = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() + toMonday);
  return monday.toISOString().slice(0, 10);
}

export function isoWeekNumber(mondayKey: string): number {
  const d = new Date(`${mondayKey}T12:00:00Z`);
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // nearest Thursday
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// ISO weekday index Mon=1 … Sun=7 (for CSS grid-column placement)
export function dayOfWeekIndex(dayKey: string): number {
  const dow = new Date(`${dayKey}T12:00:00Z`).getUTCDay(); // 0=Sun
  return dow === 0 ? 7 : dow;
}

// YYYY-MM key based on the Thursday of the ISO week (same rule as ISO week year)
export function weekMonthKey(mondayKey: string): string {
  const thursday = new Date(`${mondayKey}T12:00:00Z`);
  thursday.setUTCDate(thursday.getUTCDate() + 3);
  return thursday.toISOString().slice(0, 7);
}

export function formatMonthHeading(monthKey: string): string {
  return new Date(`${monthKey}-15T12:00:00Z`).toLocaleDateString('nb-NO', {
    month: 'long',
    year: 'numeric',
  });
}

// Short label for use in breadcrumbs: "ons 24. mai"
export function formatDayBreadcrumb(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00Z`).toLocaleDateString('nb-NO', {
    timeZone: OSLO_TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

// "Mandag 15. — Tirsdag 16." — both calendar dates that span the broadcast day
export function formatDayHeader(dayKey: string): string {
  const start = new Date(`${dayKey}T12:00:00Z`);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 1);
  const fmt = (d: Date) =>
    d.toLocaleDateString('nb-NO', {
      timeZone: OSLO_TZ,
      weekday: 'long',
      day: 'numeric',
    });
  return `${fmt(start)} — ${fmt(end)}`;
}

export function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('nb-NO', {
    timeZone: OSLO_TZ,
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface DayGroup {
  dayKey: string;
  rounds: Round[];
}

export interface WeekGroup {
  mondayKey: string;
  weekNumber: number;
  days: DayGroup[];
}

export interface MonthGroup {
  monthKey: string;
  weeks: WeekGroup[];
}

export interface Grouped {
  months: MonthGroup[];
  undated: Round[];
}

export function groupRounds(list: Round[]): Grouped {
  const dayMap = new Map<string, Round[]>();
  const undated: Round[] = [];

  for (const round of list) {
    const dayKey = broadcastDayKey(round);
    if (!dayKey) {
      undated.push(round);
      continue;
    }
    const existing = dayMap.get(dayKey) ?? [];
    existing.push(round);
    dayMap.set(dayKey, existing);
  }

  // Sort rounds within each day by start time, then createdAt
  for (const dayRounds of dayMap.values()) {
    dayRounds.sort((a, b) => {
      const byLocksAt = (a.schedule?.locksAt ?? 0) - (b.schedule?.locksAt ?? 0);
      if (byLocksAt !== 0) return byLocksAt;
      return a.createdAt - b.createdAt;
    });
  }

  // Group days into ISO weeks
  const weekMap = new Map<string, DayGroup[]>();
  for (const [dayKey, dayRounds] of dayMap.entries()) {
    const mondayKey = weekMondayKey(dayKey);
    const days = weekMap.get(mondayKey) ?? [];
    days.push({ dayKey, rounds: dayRounds });
    weekMap.set(mondayKey, days);
  }

  const sortedWeeks: WeekGroup[] = [...weekMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([mondayKey, days]) => ({
      mondayKey,
      weekNumber: isoWeekNumber(mondayKey),
      days: [...days].sort((a, b) => a.dayKey.localeCompare(b.dayKey)),
    }));

  // Group weeks by month
  const monthMap = new Map<string, WeekGroup[]>();
  for (const week of sortedWeeks) {
    const monthKey = weekMonthKey(week.mondayKey);
    const existing = monthMap.get(monthKey) ?? [];
    existing.push(week);
    monthMap.set(monthKey, existing);
  }

  const months: MonthGroup[] = [...monthMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monthKey, weeks]) => ({ monthKey, weeks }));

  return { months, undated };
}
